import { immerable } from "immer";

import type { CallDirection, ConferenceParticipant, HoldState, VoiceTask } from "@/sdk";
import type { AgentActivity, ChannelKind, TaskStatus, TaskStatusReason } from "@/sdk/agent/types";
import type { CaseDetails } from "@/shared-types";

import { tfnToMaskedName } from "@/shared-utils";

import { getProfileById } from "./hooks/useTrainingProfiles";

function generateRandomPhoneNumber() {
    const randomDigit = ({ minimum = 0 } = {}) => Math.floor(Math.random() * (10 - minimum) + minimum);

    const getValidCode = () => {
        const first = randomDigit({ minimum: 2 });
        const second = randomDigit();
        const third = randomDigit();
        return `${first}${second}${third}`;
    };

    const areaCode = getValidCode();
    const exchangeCode = "555"; // Commonly used for testing
    const subscriberNumber = `${randomDigit()}${randomDigit()}${randomDigit()}${randomDigit()}`;

    return `(${areaCode}) ${exchangeCode}-${subscriberNumber}`;
}

interface ExpertUniversitySimulatedVoiceTaskProps {
    partner: string;
    profileId: string;
    mdn: string;
}

export class ExpertUniversitySimulatedVoiceTask implements VoiceTask {
    public readonly sessionId: string;
    [immerable] = true;

    public readonly onAcceptStatus = "Busy" as AgentActivity;

    conferenceParticipants: ConferenceParticipant[];
    public callerId: string;
    public mdn: string;
    public readonly id: string;
    public readonly profileId: string;
    public readonly partner: string;
    public readonly dateCreated: Date;
    public readonly type: ChannelKind;
    public readonly autoAccept: boolean;
    public readonly status: TaskStatus;
    public statusReason: TaskStatusReason | undefined;
    public statusUpdatedAt: Date | undefined;
    public readonly kind: string;
    public callDirection: CallDirection;
    public isMuted: boolean;
    public holdState: HoldState;
    public conferenceStarted: boolean;
    public customerLeftTheConference: boolean;
    public readonly toLog: () => Record<string, unknown>;

    constructor({ partner, profileId, mdn }: ExpertUniversitySimulatedVoiceTaskProps) {
        this.mdn = mdn;
        this.callerId = mdn;
        this.callDirection = "inbound"; // Default to inbound, will be updated later
        const id = `${crypto.randomUUID()}-test-training`;
        this.id = id;
        this.sessionId = id;
        this.holdState = {
            onHold: false,
            onHoldSince: null,
        };
        this.conferenceStarted = true;
        this.autoAccept = true;
        this.partner = partner;
        this.conferenceParticipants = [];
        this.profileId = profileId;
        this.isMuted = false;
        this.status = "assigned";
        this.statusReason = "SessionEnded";
        this.statusUpdatedAt = new Date();
        this.customerLeftTheConference = false;
        this.dateCreated = new Date();
        this.type = "voice" as ChannelKind;
        this.kind = "with-customer";
        this.toLog = () => {
            return {
                conferenceId: this.conferenceId,
                agentCallId: this.agentCallId,
                customerCallId: this.customerCallId,
                asurionCallId: this.asurionCallId,
                customerOriginalCallId: this.customerOriginalCallId,
                callDirection: this.callDirection,
                isMuted: this.isMuted,
                holdState: this.holdState,
                conferenceStarted: this.conferenceStarted,
                customerLeftTheConference: this.customerLeftTheConference,
                conferenceParticipants: this.conferenceParticipants.map(({ participantName, ...rest }) => ({
                    participantName: tfnToMaskedName(participantName),
                    ...rest,
                })),
            };
        };
    }

    public readonly hasCustomer = false;

    public isAgentBusy(agentActivity: AgentActivity): boolean {
        return this.status === "assigned" && agentActivity === "Busy";
    }

    public get name() {
        return "Expert University Simulated Voice Task";
    }

    public get conferenceId() {
        return "sample-conference-id";
    }

    public get agentCallId() {
        return this.sessionId;
    }

    public get sessionIdFallback() {
        return this.sessionId;
    }

    public get wrappingDuration() {
        return 30;
    }

    public get assignedAt() {
        return this.dateCreated;
    }

    public get analyticsData() {
        return {};
    }

    public updateCaseDetails(_caseDetailsUpdates: Partial<CaseDetails>): Promise<void> {
        return Promise.resolve();
    }

    public get caseDetails() {
        return undefined;
    }

    public get previousTaskId() {
        return undefined;
    }

    public get locale() {
        return undefined;
    }

    public get conversations(): Record<string, string> | undefined {
        return undefined;
    }

    public get customerCallId() {
        return undefined;
    }

    public get asurionCallId(): string | undefined {
        return undefined;
    }

    public get client() {
        return undefined;
    }

    public get clientFriendlyName() {
        return undefined;
    }

    public get routingQueue() {
        return undefined;
    }

    public get customerOriginalCallId() {
        return undefined;
    }

    public get outboundKind() {
        return null;
    }

    public get userToUser() {
        return undefined;
    }

    public get whisper() {
        return undefined;
    }

    public get ivrInfo() {
        return undefined;
    }

    public get supportNumberCalled(): string | undefined {
        return undefined;
    }

    public get lineOfBusiness(): string | undefined {
        return undefined;
    }
}

export async function createSimulatedVoiceTaskForExpertUniversity(partner: string, profileId: string) {
    const profile = await getProfileById(profileId);
    const mdn = profile?.customerIdentifier ?? generateRandomPhoneNumber();
    const task = new ExpertUniversitySimulatedVoiceTask({ partner, profileId, mdn });

    return { task, simulationId: profile?.simulationId };
}
